import type { ReactNode } from 'react'
import type { LogisticsTabValue } from '~/routes/_authenticated/logistics/-search-schema'

/**
 * Logique pure de la barre d'onglets Logistique, sans React ni registre de routes : testée par
 * `logistics-tabs-state.test.mjs`.
 */

interface TabDefinition<TValue extends string> {
  value: TValue
  /** Onglet annoncé mais pas encore livré : toujours désactivé, jamais sélectionné. */
  comingSoon?: boolean
}

interface TabAuthorization {
  authorized: boolean
  tooltip?: ReactNode
}

/**
 * Onglets prêts pour `AppTabs` (un onglet interdit reste affiché, désactivé avec son tooltip) et
 * onglet actif : celui de l'URL s'il est ouvert, sinon le premier onglet ouvert.
 * `authorizations[i]` est la résolution des droits de `tabs[i]` (`useAuthorizedTabs`).
 */
export const resolveLogisticsTabs = <TValue extends string, TTab extends TabDefinition<TValue>>(
  tabs: readonly TTab[],
  authorizations: readonly TabAuthorization[],
  requestedTab: TValue,
): { tabs: (TTab & { disabled: boolean; tooltip?: ReactNode })[]; activeTab: TValue } => {
  const resolved = tabs.map((tab, index) =>
    tab.comingSoon
      ? { ...tab, disabled: true, tooltip: 'Bientôt' }
      : {
          ...tab,
          disabled: !authorizations[index]?.authorized,
          tooltip: authorizations[index]?.tooltip,
        },
  )
  const isOpen = (value: TValue) => resolved.some((tab) => tab.value === value && !tab.disabled)
  const activeTab = isOpen(requestedTab)
    ? requestedTab
    : (resolved.find((tab) => !tab.disabled)?.value ?? requestedTab)
  return { tabs: resolved, activeTab }
}

/** Onglets référentiels : les seuls qui lisent le configurateur industriel (ateliers). */
const REFERENCE_TABS: ReadonlySet<LogisticsTabValue> = new Set(['hubs', 'vehicles', 'hr'])

/**
 * Données lues par l'onglet actif : une requête n'est activée, et son erreur affichée, que pour
 * l'onglet qui la consomme (une requête désactivée garde son erreur en cache).
 */
export const logisticsTabDataNeeds = (activeTab: LogisticsTabValue) => ({
  configurator: REFERENCE_TABS.has(activeTab),
  collaborators: activeTab === 'hr',
  selectorOptions: activeTab === 'simulator',
})
